import {
  Activity,
  Award,
  BookOpen,
  LayoutDashboard,
  MessageSquare,
  Video,
  Users,
  ListChecks,
} from "lucide-react";

/**
 * Navigation catalog for the Admin portal.
 *
 * Shape mirrors the Teacher and Student catalogs so the shared portal layout and bottom bar can
 * consume it identically; only the entries differ. One module owns the tab's path, labels, and
 * module tone, so the nav bar and the layout's header subtitle cannot drift apart.
 * The first entry is the portal landing tab; keep auth's admin home aligned with it.
 */
export const ADMIN_NAVIGATION = [
  {
    id: "help",
    segment: "help",
    path: "/admin/help",
    subtitle: "راهنمای استفاده از کایرسافت",
    title: "راهنما",
    icon: BookOpen,
    lazy: async () =>
      (await import("@/features/admin-help/route")).loadHelpLandingRoute(),
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "recordings",
    segment: "recordings",
    path: "/admin/recordings",
    subtitle: "کلاس‌های مدرسه و ضبط‌ها",
    /** The classes shell owns its secondary navigation and lazily mounts the selected section. */
    lazy: async () => ({
      Component: (await import("@/features/admin-portal")).AdminClassesPage,
    }),
    title: "کلاس مجازی",
    icon: Video,
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "absences",
    segment: "absences",
    path: "/admin/absences",
    subtitle: "گزارش غیبت کلاس‌ها",
    /** Daily attendance remains a separate destination after the primary classroom tab. */
    lazy: async () => ({
      Component: (await import("@/features/admin-portal")).AdminAbsencesPage,
    }),
    title: "غیبت‌ها",
    icon: LayoutDashboard,
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "messages",
    segment: "messages",
    path: "/admin/messages",
    subtitle: "ارسال پیام و مشاهده نتایج",
    /** The router places this entry inside the portal and its deeper workflow pages outside it. */
    lazy: async () =>
      (
        await import("@/features/admin-messaging/route")
      ).loadMessageEntryRoute(),
    title: "پیام‌ها",
    icon: MessageSquare,
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "people",
    segment: "people",
    path: "/admin/people",
    subtitle: "کادر مدرسه، دانش‌آموزان و کلاس‌ها",
    /** People lists live in the portal; their editors own standalone detail routes. */
    lazy: async () =>
      (await import("@/features/admin-people/route")).loadPeopleListRoute(),
    title: "مدرسه",
    icon: Users,
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "monitoring",
    segment: "monitoring",
    path: "/admin/monitoring",
    subtitle: "بررسی شواهد عملکرد دانش‌آموزان",
    title: "پایش",
    icon: Activity,
    lazy: async () =>
      (await import("@/features/admin-monitoring/route")).loadMonitoringRoute(),
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "conduct",
    segment: "conduct",
    path: "/admin/conduct",
    subtitle: "ثبت تشویق و موارد انضباطی دانش‌آموزان",
    title: "تشویق و انضباط",
    icon: Award,
    lazy: async () =>
      (await import("@/features/admin-conduct/route")).loadConductRoute(),
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
  {
    id: "polls",
    segment: "polls",
    path: "/admin/polls",
    subtitle: "نظرسنجی و پاسخ مخاطبان",
    title: "نظرسنجی‌ها",
    icon: ListChecks,
    lazy: async () =>
      (await import("@/features/polls/route")).loadPollPortalRoute(),
    activeColor: "text-module-sky-600",
    activeBg: "bg-module-sky-100",
    activeBorder: "border-module-sky-200",
  },
] as const;
